import { useState, useEffect } from 'react'
import { apiService } from '../../services/api'

/**
 * Reusable product group dropdown.
 *
 * Fetches groups from GET /operators/{code}/product-groups and renders a
 * <select>. Automatically hides itself when the operator has only one group
 * (DEFAULT) — the selector adds no value in that case.
 *
 * value=null means "no filter" and renders as "Wszystkie" when showAll=true.
 * The component never auto-selects a value — the parent controls state fully.
 *
 * Props:
 *   operatorCode  – e.g. "ORANGE"
 *   value         – currently selected group code (null = no filter / all)
 *   onChange       – (groupCode: string | null) => void
 *   showAll        – if true, include "Wszystkie" option for null (default true)
 *   className      – extra CSS classes on the wrapper div
 */
export default function ProductGroupSelector({
  operatorCode,
  value,
  onChange,
  showAll = true,
  className = '',
}) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Clear immediately to prevent stale data from previous operator
    setGroups([])
    if (!operatorCode) return

    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await apiService.getProductGroups(operatorCode)
        if (!cancelled) setGroups(res.data.groups || [])
      } catch {
        if (!cancelled) setGroups([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [operatorCode])

  const explicitGroups = groups.filter(g => !g.is_default && !g.is_aggregate)

  // When the selector would be hidden (≤1 meaningful choice), auto-report the
  // only available group up so the parent form has a non-null value. Prefer
  // the single explicit group; fallback to the default group if that's all
  // that exists.
  useEffect(() => {
    if (showAll) return
    if (loading || groups.length === 0) return
    const known = explicitGroups.some(g => g.code === value)
    if (known) return
    const auto = explicitGroups[0]?.code
      || groups.find(g => g.is_default)?.code
      || groups[0]?.code
    if (auto && value !== auto) onChange(auto)
  }, [showAll, loading, groups, explicitGroups, value, onChange])

  // Hide if operator has no meaningful product group choices
  if (explicitGroups.length <= 1) return null

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Grupa produktów
      </label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        disabled={loading}
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                   disabled:opacity-50 disabled:cursor-wait"
      >
        {showAll && <option value="">Wszystkie</option>}
        {explicitGroups.map(g => (
          <option key={g.code} value={g.code}>{g.name}</option>
        ))}
      </select>
    </div>
  )
}
